import React, { FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import { Box, Chip, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import { type TFunction } from 'i18next';

import { getGetUserByIdQueryKey, PlatformLocale, useGetCurrentUser, UserDTO } from '@/api/generated';
import DropdownSelect from '@/components/fields/DropdownSelect';
import Field from '@/components/fields/Field';
import GeneralStatusDropdownSelect from '@/components/fields/GeneralStatusDropdownSelect';
import TextInput from '@/components/fields/TextInput';
import { UserAvatar } from '@/components/UserAvatar';
import { useIsMidotUser } from '@/hooks/permissions/useIsMidotUser';
import { usePlatformPermissions } from '@/hooks/permissions/usePlatformPermissions';
import { ReadonlyProvider, useReadonly } from '@/hooks/useReadonly';
import { useUserStatusActions } from '@/hooks/useUserStatusActions';
import typography from '@/theme/typography';
import { dateFormatOptions, timeZoneOptions } from '@/utils/dateTimeOptions';
import OfficeDropdownSelect from '@/views/fields/OfficeDropdownSelect/OfficeDropdownSelect';

import PhoneUpdateInput from './PhoneUpdateInput';

const languageOptions = (t: TFunction) =>
  Object.keys(PlatformLocale).map(language => ({
    value: language,
    label: t(`common:langs.${language}`)
  }));

const MAX_WIDTH = 1000;

const AvatarUpload: FC = () => {
  const { watch } = useFormContext();
  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const fullName = watch('fullName');
  const user = { firstName, lastName, fullName };

  return <UserAvatar user={user} size={72} />;
};

enum UserSettingsBasicInfoTabMode {
  CLIENT = 'client',
  EMPLOYEE = 'employee'
}

const UserSettingsBasicInfoTab: FC = () => {
  const { t } = useTranslation(['users', 'settings']);
  const { spacing } = useTheme();
  const { watch, setValue } = useFormContext();
  const { userId } = useParams<{ userId: string }>();
  const queryClient = useQueryClient();
  const isMidotEmployee = watch('isMidotEmployee');
  const mode = isMidotEmployee ? UserSettingsBasicInfoTabMode.EMPLOYEE : UserSettingsBasicInfoTabMode.CLIENT;

  const currentStatus = watch('status');
  const { isReadonly } = useReadonly();

  const setUserStatus = useCallback(
    (newUserStatus: string) => {
      if (!userId || !newUserStatus) return;

      const userQueryKey = getGetUserByIdQueryKey(userId);
      queryClient.setQueryData(
        userQueryKey,
        (oldData: UserDTO | undefined) => ({ ...oldData!, status: newUserStatus }) satisfies UserDTO
      );
      setValue('status', newUserStatus);
    },
    [queryClient, setValue, userId]
  );

  const {
    change: handleStatusChange,
    isLoading: isStatusLoading,
    availableStatuses
  } = useUserStatusActions({
    userId: userId!,
    currentStatus,
    onChange: setUserStatus
  });

  const { value: { canAssignMidotRoles } = {} } = usePlatformPermissions();
  const { isMidotUser } = useIsMidotUser();
  const { data: currentUser } = useGetCurrentUser();
  const isOwnProfile = currentUser?.id === userId;

  return (
    <Stack
      width="100%"
      sx={{
        '& .MuiFormLabel-root': { textAlign: 'start' },
        '& .MuiFormControl-root': { pb: 0 },
        '& .DropdownSelect-expandMoreIcon': { color: 'text.primary' }
      }}
    >
      <Stack direction="row" justifyContent="space-between" width="100%" p={3}>
        <Typography sx={{ ...typography.body1, fontWeight: typography.fontWeightMedium }}>
          {t('users.sections.basicInfo')}
        </Typography>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography sx={{ ...typography.body3, fontWeight: typography.fontWeightMedium, color: 'grey.700' }}>
            {t('users.fields.status')}
          </Typography>

          <Stack direction="row" alignItems="center" sx={{ position: 'relative', mx: 1 }}>
            <GeneralStatusDropdownSelect
              name="status"
              value={currentStatus}
              onChange={handleStatusChange}
              activeOptions={availableStatuses as string[]}
              isDisabled={isStatusLoading || isReadonly || (!isMidotUser && isOwnProfile)}
              sx={{
                mx: 1,
                '& .DropdownSelect-control': {
                  minWidth: 122,
                  minHeight: 'unset',
                  '& .DropdownSelect-optionIcon .CircleIcon-root': {
                    m: 0.25
                  },
                  '& .DropdownSelect-optionText .MuiTypography-root': {
                    ...typography.body3,
                    lineHeight: 'calc(4 / 3)'
                  }
                },
                '& .DropdownSelect-indicator': {
                  p: 0.5
                }
              }}
            />

            {isStatusLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%',
                  left: '100%'
                }}
              >
                <CircularProgress size={20} />
              </Box>
            )}
          </Stack>
        </Stack>
      </Stack>

      <Stack px={3} mb={3} gap={3} direction="row" alignItems="end">
        <AvatarUpload />
      </Stack>

      {isMidotUser && (
        <Stack direction="row" alignItems="center" gap={1} px={3} mb={3}>
          <Typography sx={{ ...typography.body3, fontWeight: typography.fontWeightMedium, color: 'grey.700' }}>
            {t('users.fields.type')}
          </Typography>
          <Chip
            label={t(`users.types.${mode}`)}
            variant="filled"
            size="small"
            sx={{
              borderRadius: spacing(0.5),
              bgcolor: 'primary.100',
              height: spacing(3),
              '& .MuiChip-label': {
                ...typography.body3,
                fontWeight: typography.fontWeightRegular,
                color: 'text.primary'
              }
            }}
          />
        </Stack>
      )}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          mx: 3,
          gridColumnGap: spacing(2),
          gridRowGap: spacing(3),
          maxWidth: MAX_WIDTH,
          '& .MuiInputBase-root': { minHeight: spacing(5) },
          '& .MuiFormControl-root': { px: 0, pt: 0 },
          '& .DropdownSelect-control': { minHeight: spacing(5) }
        }}
      >
        <Field
          component={TextInput}
          name="fullName"
          label={t('users.fields.fullName')}
          withFormControl
          markAsRequired
        />
        <Field component={TextInput} name="title" label={t('users.fields.title')} withFormControl />
        {isMidotEmployee ? (
          <ReadonlyProvider readonly={!canAssignMidotRoles || isReadonly}>
            <Field component={OfficeDropdownSelect} name="offices" label={t('users.fields.offices')} withFormControl />
          </ReadonlyProvider>
        ) : (
          <Box />
        )}
        <Field
          component={TextInput}
          name="email"
          label={t('users.fields.email')}
          disabled
          withFormControl
          markAsRequired
        />
        <PhoneUpdateInput />
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          mx: 3,
          mt: 3,
          gridColumnGap: spacing(2),
          gridRowGap: spacing(3),
          maxWidth: MAX_WIDTH,
          '& .MuiInputBase-root': { minHeight: spacing(5) },
          '& .MuiFormControl-root': { px: 0, pt: 0 },
          '& .DropdownSelect-control': { minHeight: spacing(5) }
        }}
      >
        <Field
          component={DropdownSelect}
          name="preferredLocale"
          label={t('users.fields.language')}
          innerProps={{
            options: languageOptions(t),
            isMulti: false,
            usePortal: true
          }}
          withFormControl
        />
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gridColumnGap: spacing(2),
            gridRowGap: spacing(3),
            maxWidth: MAX_WIDTH
          }}
        >
          <Field
            component={DropdownSelect}
            name="preferredDateFormat"
            label={t('users.fields.dateFormat')}
            innerProps={{
              options: dateFormatOptions,
              isMulti: false,
              usePortal: true
            }}
            withFormControl
          />
          <Field
            component={DropdownSelect}
            name="preferredTimezone"
            label={t('users.fields.timeZone')}
            innerProps={{
              options: timeZoneOptions,
              isMulti: false,
              usePortal: true
            }}
            withFormControl
          />
        </Box>
      </Box>
      {isMidotUser && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            mx: 3,
            mt: 3,
            gridColumnGap: spacing(2),
            gridRowGap: spacing(3),
            maxWidth: MAX_WIDTH,
            '& .MuiFormControl-root': { px: 0, pt: 0 },
            '& .MuiInputBase-root': { minHeight: spacing(5) }
          }}
        >
          <Field
            component={TextInput}
            name="id"
            label={t('users.fields.userNumberNewSystem')}
            disabled
            withFormControl
          />
          <Field component={TextInput} name="legacyId" label={t('users.fields.userNumberOldPodium')} withFormControl />
        </Box>
      )}
    </Stack>
  );
};

export default UserSettingsBasicInfoTab;
